import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ReadinessResult } from "@jilian/core";
import { evaluateReadiness, generateWorkoutPlan } from "@jilian/core";
import { useSessionStore, completedSetCount } from "../state/useSessionStore";
import { getSettings, DEFAULT_INJURY } from "../db/repo";
import { loadLatestWeights } from "../db/workoutRepo";
import { EXERCISE_LIBRARY } from "../data/exercises";
import { formatElapsed } from "../lib/format";
import { ExerciseCard } from "../components/ExerciseCard";
import { RestTimer } from "../components/RestTimer";
import { ReplacementSheet } from "../components/ReplacementSheet";
import { Button, Sheet } from "../components/ui";

import { DEFAULT_TEMPLATES, type SequenceCode } from "@jilian/core";
import { useSettings, useTrainingTemplates } from "../db/hooks";

/** 按正常准备度生成序列计划（兜底入口用；正常流程在「今日」页按恢复问询生成）。 */
async function buildNormalPlan(
  sequence: string = "A",
  userId: string = "user-default",
): Promise<{ exercises: ReturnType<typeof generateWorkoutPlan>["exercises"]; readiness: ReadinessResult }> {
  const settings = await getSettings();
  const injury = settings?.injury ?? DEFAULT_INJURY;
  const lastWeights = await loadLatestWeights(userId);
  const readiness = evaluateReadiness({
    sleepQuality: "good",
    fatigueLevel: "low",
    muscleSoreness: "none",
    footStatus: "stable",
    jointPain: "none",
  });
  const slots = DEFAULT_TEMPLATES[sequence as SequenceCode] ?? DEFAULT_TEMPLATES.A;
  const plan = generateWorkoutPlan({ sequenceCode: sequence, slots, readiness, injury, exerciseLibrary: EXERCISE_LIBRARY, lastWeights });
  return { exercises: plan.exercises, readiness };
}

export function TrainingSession() {
  const nav = useNavigate();
  const store = useSessionStore();
  const hydrate = useSessionStore((s) => s.hydrateFromDb);
  const settings = useSettings();
  const userId = settings?.profile?.id ?? store.userId ?? "user-default";
  const [now, setNow] = useState(() => Date.now());
  const [replacing, setReplacing] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [earlyEndModal, setEarlyEndModal] = useState(false);

  const startedAt = store.startedAt;
  const lastCompleted = store.lastCompleted;

  // 恢复未完成训练：进入页面即从 IndexedDB 加载进行中的会话（刷新不丢失）。
  useEffect(() => {
    let mounted = true;
    hydrate().then(() => {
      if (mounted) setReady(true);
    });
    return () => {
      mounted = false;
    };
  }, [hydrate]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // 3 秒撤销计时器自动清除
  useEffect(() => {
    if (!lastCompleted) return;
    const timer = window.setTimeout(() => {
      store.clearLastCompleted();
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [lastCompleted, store]);

  const elapsed = startedAt ? Math.max(0, Math.floor((now - startedAt) / 1000)) : 0;
  const doneSets = completedSetCount(store.exercises);
  const templates = useTrainingTemplates(userId);
  const seqName = templates?.find((s) => s.code === store.sequence)?.name ?? `序列 ${store.sequence}`;

  const handleFinishNormal = () => {
    store.endSession();
    nav("/summary");
  };

  const handleFinishEarly = (reason: string) => {
    store.endSessionEarly(reason);
    setEarlyEndModal(false);
    nav("/summary");
  };

  if (!ready) {
    return (
      <div className="min-h-screen mx-auto max-w-[480px] px-4 pt-safe-top flex items-center justify-center text-tone-soft">
        正在恢复训练…
      </div>
    );
  }

  // 若直接进入且未开始，给出兜底（按正常准备度生成序列 A）。
  if (store.exercises.length === 0 || store.startedAt == null) {
    return (
      <div className="min-h-screen mx-auto max-w-[480px] px-4 pt-safe-top flex flex-col items-center justify-center text-center p-6">
        <p className="text-tone-soft mb-4 font-caption">当前没有正在进行的训练会话。</p>
        <Button
          onClick={() => {
            void buildNormalPlan("A").then((p) => store.start("A", p.exercises, p.readiness));
          }}
        >
          开始序列 A（胸 · 三头 · 腿屈伸）
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen mx-auto max-w-[480px] pb-36">
      {/* 顶栏 */}
      <header className="sticky top-0 z-30 glass-panel pt-safe-top px-4 pb-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[18px] font-bold text-tone-ink tracking-tight font-title">训练中 · {seqName}</div>
            <div className="text-[12px] font-medium text-tone-soft flex items-center gap-2 mt-0.5 font-caption">
              <span>已用 <span className="font-bold text-tone-ink tabular-nums">{formatElapsed(elapsed)}</span></span>
              <span>•</span>
              <span>已完成 <span className="font-bold text-tone-good tabular-nums">{doneSets}</span> 组</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setEarlyEndModal(true)}
              className="text-[12px] font-medium text-tone-soft hover:text-tone-danger px-2.5 py-1.5 rounded-elem active:scale-95 transition-all"
            >
              提前结束
            </button>
            <Button
              variant="primary"
              className="min-h-[38px] px-3.5 text-xs font-bold rounded-elem"
              onClick={handleFinishNormal}
            >
              完成
            </Button>
          </div>
        </div>
      </header>

      {/* 动作列表 */}
      <div className="px-4 pt-4">
        {store.exercises.map((item) => (
          <ExerciseCard
            key={item.exerciseId}
            item={item}
            isActive={item.exerciseId === store.activeExerciseId}
            onReplace={(id) => setReplacing(id)}
          />
        ))}
      </div>

      {/* 3 秒「撤销」浮条 Toast */}
      {lastCompleted && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-32px)] max-w-[440px] bg-tone-card border border-tone-border shadow-2xl rounded-elem p-3 flex items-center justify-between animate-fade-in">
          <div className="flex items-center gap-2 text-[13px] font-semibold text-tone-ink">
            <span className="w-2 h-2 rounded-full bg-tone-good animate-ping" />
            <span>已记录第 {lastCompleted.setIndex} 组</span>
          </div>
          <button
            type="button"
            onClick={() => store.undoLastSet()}
            className="text-[13px] font-bold text-tone-accent px-3 py-1.5 rounded-elem bg-tone-accentSoft active:scale-95"
          >
            撤销 (3s)
          </button>
        </div>
      )}

      {/* 底部全宽大完成按钮（盲操优先，≥56px 高度） */}
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] glass-dock border-t border-tone-border px-4 py-3 pb-safe-bottom z-30">
        <Button
          className="w-full min-h-[56px] text-[16px] font-bold shadow-sm"
          onClick={handleFinishNormal}
        >
          完成本次训练 · 查看进阶与评估
        </Button>
      </div>

      {/* 提前结束训练理由弹窗 */}
      {earlyEndModal && (
        <Sheet
          open={earlyEndModal}
          onClose={() => setEarlyEndModal(false)}
          title="提前结束训练"
        >
          <div className="space-y-2.5">
            <p className="text-[13px] text-tone-soft mb-3 font-caption">
              请选择提前结束的原因，系统将结算已完成组数，并在后续进阶算法中做针对性保护：
            </p>
            {[
              { id: "lack_of_time", label: "时间不够 / 突发事情" },
              { id: "excessive_fatigue", label: "今日体力不支 / 肌肉深层疲劳" },
              { id: "joint_discomfort", label: "关节或足部感到不适" },
              { id: "injury_pain", label: "出现疼痛，主动保护停止" },
            ].map((r) => (
              <button
                key={r.id}
                onClick={() => handleFinishEarly(r.id)}
                className="w-full min-h-[50px] px-4 py-3 rounded-elem bg-tone-card border border-tone-border flex items-center justify-between active:scale-[0.99] transition-all hover:border-tone-danger text-left"
              >
                <span className="text-[14px] font-semibold text-tone-ink">{r.label}</span>
                <span className="text-tone-danger text-xs font-bold">结束并结算</span>
              </button>
            ))}
          </div>
        </Sheet>
      )}

      <RestTimer />
      <ReplacementSheet fromExerciseId={replacing ?? ""} open={replacing != null} onClose={() => setReplacing(null)} />
    </div>
  );
}
